import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  renderInlineMarkup,
  renderInlineMarkupSplit,
} from '@source-components-react/shared/inline-markup';
import {
  PLACEHOLDER_VALUES_PROP,
  fillPlaceholders,
  resolvePlaceholderValues,
} from '@source-components-react/shared/placeholders';
import type { PageViewType, WidgetInstanceProps } from '@source-components-react/types';
import { DEFAULT_CONTENT_TEXT } from './default-attribute-values';
import { resolveEnclosingTag } from './resolve-enclosing-tag';
import type { EnclosingTag } from './enclosing-tags';
import {
  resolveTextShowLessLabel,
  resolveTextShowMoreAfter,
  resolveTextShowMoreLabel,
} from './text-attributes';

/**
 * How this render fills `{{ … }}` placeholders. On the composer canvas they
 * stay as the author typed them: the composer never passes host values, so
 * filling them there would only blank them out.
 */
const placeholderFillerFor = (
  pageViewType: PageViewType | undefined,
  widgetInstanceProps: WidgetInstanceProps | undefined
): ((words: string) => string) | undefined => {
  if (pageViewType === 'Compose') {
    return undefined;
  }
  const values = resolvePlaceholderValues(widgetInstanceProps?.[PLACEHOLDER_VALUES_PROP]);
  return (words) => fillPlaceholders(words, values);
};

/**
 * Keeps the checkbox focusable and invisible. The toggle's accessible name
 * comes from the label's visible text, not from this input's own rendering.
 */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

const rootProps = (
  styleProps: { [key: string]: any } | undefined,
  className: string | undefined,
  truncated: boolean
): Record<string, unknown> => {
  const props: Record<string, unknown> =
    className !== undefined ? { style: styleProps, className } : { style: styleProps };
  if (truncated) {
    props['data-rulecms-text-truncate'] = '';
  }
  return props;
};

/**
 * Head, ellipsis, rest, and the checkbox toggle. The `hidden` attributes are
 * progressive enhancement: without the library stylesheet (or without
 * `:has()`), the reader sees the full text and no toggle.
 */
const renderTruncatedContent = (
  head: ReactNode,
  tail: ReactNode,
  showMoreLabel: ReactNode,
  showLessLabel: ReactNode
): ReactNode => (
  <>
    {head}
    <span data-rulecms-text-ellipsis="" aria-hidden="true" hidden>{'\u2026'}</span>
    <span data-rulecms-text-rest="">{tail}</span>{' '}
    <label data-rulecms-text-toggle="" hidden>
      <input type="checkbox" data-rulecms-text-toggle-input="" style={VISUALLY_HIDDEN} />
      <span data-rulecms-text-show-more="">{showMoreLabel}</span>
      <span data-rulecms-text-show-less="">{showLessLabel}</span>
    </label>
  </>
);

export function RText({
  enclosingTag,
  componentAttributes,
  styleProps,
  className,
  widgetInstanceProps,
  pageViewType,
}: Readonly<{
  /**
   * HTML tag to enclose the text in (e.g. 'p', 'h1', 'span'). Restricted to
   * tags that hold text without changing its behavior. When omitted the text
   * renders bare, exactly as before this attribute existed — unless a show-more
   * limit is set, in which case the root is a `span` so the toggle has an
   * element to live in.
   */
  enclosingTag?: EnclosingTag;
  componentAttributes?: { [key: string]: any };
  /**
   * Column styles from the widget pipeline. Applied to the enclosing tag when
   * there is one; bare text has no element to carry them, in which case
   * `resolveNoContainer` has already told widget-react to use a wrapper div.
   */
  styleProps?: { [key: string]: any };
  /**
   * Host-app classes for this column, when the renderer decided this card
   * carries them. Applied to the enclosing tag; ignored for bare text (the
   * renderer has already taken responsibility for the class).
   */
  className?: string;
  /**
   * Host-app props for this instance. `placeholderValues` — an object, or a
   * JSON string holding one — fills the `{{ … }}` placeholders in the text;
   * every other key is ignored.
   */
  widgetInstanceProps?: WidgetInstanceProps;
  /**
   * `'Compose'` on the composer canvas, where placeholders show as written.
   * Any other value, or none, fills them in.
   */
  pageViewType?: PageViewType;
}>) {
  const contentVal =
    componentAttributes?.['content-text-3-resolutions'] ?? DEFAULT_CONTENT_TEXT;

  const fillText = placeholderFillerFor(pageViewType, widgetInstanceProps);
  const links = componentAttributes?.['links-3-resolutions'];
  const spanStyles = componentAttributes?.['inline-styles-3-resolutions'];
  const showMoreAfter = resolveTextShowMoreAfter(componentAttributes);
  const resolvedTag = resolveEnclosingTag(componentAttributes, enclosingTag);
  // A configured limit with no enclosing tag always produces a span root —
  // truncated or not — so this agrees with `resolveRendersOwnElement`, which
  // can see the limit but not whether placeholders will make the text fit.
  const tag: EnclosingTag | undefined =
    resolvedTag ?? (showMoreAfter !== undefined ? 'span' : undefined);

  if (typeof contentVal !== 'string') {
    if (tag) {
      return React.createElement(tag, rootProps(styleProps, className, false), contentVal);
    }
    return contentVal;
  }

  // Marks (`{b|word}`), line breaks (`{br|}`), and the two index markers —
  // `{n|…}` into the link list and `{.n|…}` into the styling list — both by
  // 1-based position. Everything the markup emits is phrasing content inside
  // the enclosing tag, never around it. `{{ … }}` placeholders are filled after
  // the markup is parsed, so a host's value is only ever text.
  if (showMoreAfter === undefined) {
    const content = renderInlineMarkup(contentVal, links, spanStyles, fillText);
    if (tag) {
      return React.createElement(tag, rootProps(styleProps, className, false), content);
    }
    return content;
  }

  const split = renderInlineMarkupSplit(contentVal, showMoreAfter, links, spanStyles, fillText);

  if (split === undefined) {
    const content = renderInlineMarkup(contentVal, links, spanStyles, fillText);
    // `tag` is always set when the limit is on (resolved tag or span).
    return React.createElement(tag!, rootProps(styleProps, className, false), content);
  }

  const labelFiller = fillText;
  const showMoreLabel = renderInlineMarkup(
    resolveTextShowMoreLabel(componentAttributes),
    undefined,
    spanStyles,
    labelFiller
  );
  const showLessLabel = renderInlineMarkup(
    resolveTextShowLessLabel(componentAttributes),
    undefined,
    spanStyles,
    labelFiller
  );

  return React.createElement(
    tag!,
    rootProps(styleProps, className, true),
    renderTruncatedContent(split.head, split.tail, showMoreLabel, showLessLabel)
  );
}
